/* eslint-disable */

import {
    Box,
    Flex,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    Button,
    useColorModeValue,
    Badge,
    IconButton,
    Icon,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    VStack,
    HStack,
    Divider,
    Avatar,
    useToast,
} from "@chakra-ui/react";
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    useReactTable,
} from "@tanstack/react-table";
import Card from "components/card/Card";
import { NavLink } from "react-router-dom";
import * as React from "react";
import { MdCancel, MdCheckCircle, MdOutlineError, MdContentCopy, MdDownload, MdReceipt } from "react-icons/md";
import { CopyIcon, ViewIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import jsPDF from 'jspdf';
import constants from "providers/Constants";

const columnHelper = createColumnHelper();

// Transaction Details Modal Component for Card Transactions
function TransactionDetailsModal({ isOpen, onClose, transaction, t }) {
    const bgColor = useColorModeValue('white', 'navy.800');
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const secondaryTextColor = useColorModeValue('gray.500', 'gray.400');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
    const receiptBg = useColorModeValue('gray.50', 'navy.900');
    const toast = useToast();
    const [showReceipt, setShowReceipt] = React.useState(false);
    const [isDownloading, setIsDownloading] = React.useState(false);

    if (!transaction) return null;

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast({
            title: t('Copied!'),
            status: 'success',
            duration: 2000,
            isClosable: true,
        });
    };

    const formatDate = (dateString) => {
        if (!dateString || isNaN(Date.parse(dateString))) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    const formatAmount = (amount, currency) => {
        const num = Number(amount);
        if (isNaN(num)) return 'N/A';
        return `${currency || ''} ${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const DetailRow = ({ label, value, copyable = false }) => (
        <Flex justify="space-between" align="center" py={2}>
            <Text color={secondaryTextColor} fontSize="sm">{label}</Text>
            <HStack>
                <Text color={textColor} fontSize="sm" fontWeight="600" textAlign="right" maxW="200px" isTruncated>
                    {value || 'N/A'}
                </Text>
                {copyable && value && (
                    <IconButton
                        size="xs"
                        variant="ghost"
                        icon={<Icon as={MdContentCopy} />}
                        onClick={() => copyToClipboard(value)}
                        aria-label="Copy"
                    />
                )}
            </HStack>
        </Flex>
    );

    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'SUCCESSFUL': return 'green';
            case 'PENDING': return 'orange';
            case 'FAILED': return 'red';
            default: return 'gray';
        }
    };

    const downloadReceipt = async () => {
        setIsDownloading(true);
        try {
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const margin = 20;
            const contentWidth = pageWidth - (margin * 2);
            let yPos = margin;

            // Helper function to center text
            const centerText = (text, y, fontSize = 12, fontStyle = 'normal') => {
                pdf.setFontSize(fontSize);
                pdf.setFont('helvetica', fontStyle);
                const textWidth = pdf.getTextWidth(text);
                pdf.text(text, (pageWidth - textWidth) / 2, y);
            };

            // Helper function to add a line of text with label and value
            const addDetailRow = (label, value, y, wrapLongValue = false) => {
                pdf.setFontSize(10);
                pdf.setFont('helvetica', 'normal');
                pdf.setTextColor(100, 100, 100);
                pdf.text(label, margin + 10, y);
                
                pdf.setTextColor(30, 30, 30);
                pdf.setFont('helvetica', 'bold');
                const valueText = String(value || 'N/A');
                
                if (wrapLongValue) {
                    // For long values, place on next line
                    y += 6;
                    pdf.setFontSize(9);
                    pdf.setFont('helvetica', 'normal');
                    pdf.text(valueText, margin + 10, y);
                    return y + 8;
                } else {
                    const valueWidth = pdf.getTextWidth(valueText);
                    pdf.text(valueText, pageWidth - margin - 10 - valueWidth, y);
                    return y + 8;
                }
            };

            // Helper to draw avatar placeholder circle with initial
            const drawAvatarPlaceholder = (x, y, size, initial) => {
                pdf.setFillColor(100, 100, 220);
                pdf.circle(x + size / 2, y + size / 2, size / 2, 'F');
                pdf.setTextColor(255, 255, 255);
                pdf.setFontSize(12);
                pdf.setFont('helvetica', 'bold');
                const textWidth = pdf.getTextWidth(initial);
                pdf.text(initial, x + (size - textWidth) / 2, y + size / 2 + 2);
            };

            // Draw receipt border
            pdf.setDrawColor(200, 200, 200);
            pdf.setLineDashPattern([2, 2], 0);
            const receiptHeight = 240;
            pdf.roundedRect(margin, margin, contentWidth, receiptHeight, 3, 3, 'S');

            yPos = margin + 10;

            // Add avatar placeholder
            const avatarSize = 20;
            const initial = (transaction.narration || 'C').charAt(0).toUpperCase();
            drawAvatarPlaceholder((pageWidth - avatarSize) / 2, yPos, avatarSize, initial);
            
            yPos += avatarSize + 10;

            // Title - Narration
            pdf.setTextColor(30, 30, 30);
            const title = transaction.narration || t('Card Transaction');
            centerText(title.length > 40 ? title.substring(0, 40) + '...' : title, yPos, 14, 'bold');
            yPos += 7;

            // Type
            pdf.setTextColor(100, 100, 100);
            centerText(transaction.type?.toUpperCase() || '', yPos, 10, 'normal');
            yPos += 10;

            // Status badge
            const status = (transaction.status || 'N/A').toUpperCase();
            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            const statusWidth = pdf.getTextWidth(status) + 10;
            const statusX = (pageWidth - statusWidth) / 2;
            
            // Status background color
            if (transaction.status?.toUpperCase() === 'SUCCESSFUL') {
                pdf.setFillColor(198, 246, 213);
                pdf.setTextColor(34, 84, 61);
            } else if (transaction.status?.toUpperCase() === 'PENDING') {
                pdf.setFillColor(254, 235, 200);
                pdf.setTextColor(116, 66, 16);
            } else if (transaction.status?.toUpperCase() === 'FAILED') {
                pdf.setFillColor(254, 215, 215);
                pdf.setTextColor(130, 39, 39);
            } else {
                pdf.setFillColor(226, 232, 240);
                pdf.setTextColor(45, 55, 72);
            }
            
            pdf.roundedRect(statusX, yPos - 4, statusWidth, 7, 3, 3, 'F');
            pdf.text(status, statusX + 5, yPos + 1);
            yPos += 15;

            // Dashed line
            pdf.setDrawColor(200, 200, 200);
            pdf.setLineDashPattern([2, 2], 0);
            pdf.line(margin + 10, yPos, pageWidth - margin - 10, yPos);
            yPos += 10;

            // Amount
            const amount = formatAmount(transaction.amount, transaction.currency);
            pdf.setTextColor(30, 30, 30);
            centerText(amount, yPos, 24, 'bold');
            yPos += 8;

            // Currency
            pdf.setTextColor(100, 100, 100);
            centerText(transaction.currency || '', yPos, 10, 'normal');
            yPos += 12;

            // Dashed line
            pdf.line(margin + 10, yPos, pageWidth - margin - 10, yPos);
            yPos += 10;

            // Transaction details
            pdf.setTextColor(30, 30, 30);
            yPos = addDetailRow(t('Reference'), transaction.reference, yPos, true);
            yPos = addDetailRow(t('Type'), transaction.type?.toUpperCase(), yPos);
            yPos = addDetailRow(t('Narration'), transaction.narration, yPos);
            yPos = addDetailRow(t('Amount'), formatAmount(transaction.amount, transaction.currency), yPos);
            yPos = addDetailRow(t('Status'), transaction.status?.toUpperCase(), yPos);
            yPos = addDetailRow(t('Date'), transaction.createdAt ? new Date(transaction.createdAt).toLocaleString() : t('N/A'), yPos);
            yPos += 5;

            // Dashed line
            pdf.line(margin + 10, yPos, pageWidth - margin - 10, yPos);
            yPos += 10;

            // Footer
            pdf.setTextColor(100, 100, 100);
            centerText(t('Thank you for using BlockMap'), yPos, 10, 'normal');
            yPos += 6;
            centerText(transaction.createdAt ? new Date(transaction.createdAt).toLocaleString() : '', yPos, 8, 'normal');

            // Save PDF
            pdf.save(`BlockMap_Card_Receipt_${transaction.reference || 'transaction'}.pdf`);

            toast({
                title: t('Receipt downloaded successfully!'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Error generating PDF:', error);
            toast({
                title: t('Failed to download receipt'),
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsDownloading(false);
        }
    };

    // Receipt View
    const ReceiptView = () => (
        <Box
            bg={receiptBg}
            borderRadius="lg"
            p={6}
            border="1px dashed"
            borderColor={borderColor}
        >
            <VStack spacing={4} align="stretch">
                {/* Receipt Header */}
                <Flex direction="column" align="center" pb={4} borderBottom="1px dashed" borderColor={borderColor}>
                    <Avatar name={transaction.narration || 'Card'} size="lg" mb={3} bg="brand.500" />
                    <Text fontSize="xl" fontWeight="bold" color={textColor} textAlign="center" noOfLines={2}>
                        {transaction.narration || t('Card Transaction')}
                    </Text>
                    <Text fontSize="sm" color={secondaryTextColor}>{transaction.type?.toUpperCase()}</Text>
                    <Badge colorScheme={getStatusColor(transaction.status)} mt={2} fontSize="sm" px={3} py={1} borderRadius="full">
                        {transaction.status?.toUpperCase()}
                    </Badge>
                </Flex>

                {/* Amount Section */}
                <Box textAlign="center" py={4} borderBottom="1px dashed" borderColor={borderColor}>
                    <Text fontSize="3xl" fontWeight="bold" color={textColor}>
                        {formatAmount(transaction.amount, transaction.currency)}
                    </Text>
                    <Text fontSize="sm" color={secondaryTextColor}>{transaction.currency}</Text>
                </Box>

                {/* Receipt Details */}
                <VStack spacing={0} align="stretch">
                    <DetailRow label={t('Reference')} value={transaction.reference} copyable />
                    <DetailRow label={t('Type')} value={transaction.type?.toUpperCase()} />
                    <DetailRow label={t('Narration')} value={transaction.narration} />
                    <DetailRow label={t('Status')} value={transaction.status?.toUpperCase()} />
                    <DetailRow label={t('Date')} value={formatDate(transaction.createdAt)} />
                </VStack>

                {/* Receipt Footer */}
                <Flex direction="column" align="center" pt={4} borderTop="1px dashed" borderColor={borderColor}>
                    <Text fontSize="xs" color={secondaryTextColor}>{t('Thank you for using BlockMap')}</Text>
                    <Text fontSize="xs" color={secondaryTextColor} mt={1}>
                        {formatDate(transaction.createdAt)}
                    </Text>
                </Flex>
            </VStack>
        </Box>
    );

    // Details View
    const DetailsView = () => (
        <VStack spacing={4} align="stretch">
            {/* Transaction Header */}
            <Flex align="center" gap={4}>
                <Avatar name={transaction.narration || 'Card'} size="md" bg="brand.500" />
                <Box flex={1}>
                    <Text fontSize="lg" fontWeight="bold" color={textColor} noOfLines={1}>
                        {transaction.narration || t('Card Transaction')}
                    </Text>
                    <Text fontSize="sm" color={secondaryTextColor}>
                        {transaction.type?.toUpperCase()}
                    </Text>
                </Box>
                <Badge colorScheme={getStatusColor(transaction.status)} fontSize="sm">
                    {transaction.status?.toUpperCase()}
                </Badge>
            </Flex>

            <Divider />

            {/* Transaction Info */}
            <Box>
                <Text fontWeight="600" color={textColor} mb={3}>{t('Transaction Information')}</Text>
                <VStack spacing={0} align="stretch" bg={receiptBg} p={4} borderRadius="lg">
                    <DetailRow label={t('Reference')} value={transaction.reference} copyable />
                    <DetailRow label={t('Type')} value={transaction.type?.toUpperCase()} />
                    <DetailRow label={t('Narration')} value={transaction.narration} />
                </VStack>
            </Box>

            {/* Amount Info */}
            <Box>
                <Text fontWeight="600" color={textColor} mb={3}>{t('Amount Details')}</Text>
                <VStack spacing={0} align="stretch" bg={receiptBg} p={4} borderRadius="lg">
                    <DetailRow label={t('Amount')} value={formatAmount(transaction.amount, transaction.currency)} />
                    <DetailRow label={t('Currency')} value={transaction.currency} />
                </VStack>
            </Box>

            {/* Status Info */}
            <Box>
                <Text fontWeight="600" color={textColor} mb={3}>{t('Status Information')}</Text>
                <VStack spacing={0} align="stretch" bg={receiptBg} p={4} borderRadius="lg">
                    <DetailRow label={t('Status')} value={transaction.status?.toUpperCase()} />
                    <DetailRow label={t('Created At')} value={formatDate(transaction.createdAt)} />
                    <DetailRow label={t('Updated At')} value={formatDate(transaction.updatedAt)} />
                </VStack>
            </Box>
        </VStack>
    );

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
            <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(5px)" />
            <ModalContent bg={bgColor} borderRadius="xl">
                <ModalHeader>
                    <Flex align="center" gap={2}>
                        <Icon as={showReceipt ? MdReceipt : ViewIcon} />
                        <Text>{showReceipt ? t('Transaction Receipt') : t('Transaction Details')}</Text>
                    </Flex>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    {showReceipt ? <ReceiptView /> : <DetailsView />}
                </ModalBody>
                <ModalFooter>
                    <Button
                        variant="outline"
                        mr={3}
                        onClick={() => setShowReceipt(!showReceipt)}
                        leftIcon={<Icon as={showReceipt ? ViewIcon : MdReceipt} />}
                    >
                        {showReceipt ? t('View Details') : t('View Receipt')}
                    </Button>
                    <Button
                        variant="outline"
                        colorScheme="green"
                        mr={3}
                        onClick={downloadReceipt}
                        leftIcon={<Icon as={MdDownload} />}
                        isLoading={isDownloading}
                        loadingText={t('Downloading...')}
                    >
                        {t('Download Receipt')}
                    </Button>
                    <Button colorScheme="brand" onClick={onClose}>
                        {t('Close')}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default function CardTransactionsTable(props) {
    const { tableData, sortArray, index } = props;
    const { t } = useTranslation();
    const [sorting, setSorting] = React.useState([]);
    const [data, setData] = React.useState(tableData);
    const [{ pageIndex, pageSize }, setPagination] = React.useState({
        pageIndex: 0,
        pageSize: 10,
    });
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [selectedTransaction, setSelectedTransaction] = React.useState(null);

    const textColor = useColorModeValue("secondaryGray.900", "white");
    const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
    const bgHover = useColorModeValue("gray.50", "whiteAlpha.10");

    const handleViewTransaction = (transaction) => {
        setSelectedTransaction(transaction);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedTransaction(null);
    };

    const copyToClipboard = React.useCallback((text, label) => {
        if (!text || text === t("N/A")) {
            constants.showToast("error", t("FAILED"), t("No data to copy"));
            return;
        }
        navigator.clipboard.writeText(text).then(() => {
            constants.showToast("success", t("COPIED"), `${label} ${t("copied to clipboard")}`);
        }).catch(() => {
            constants.showToast("error", t("FAILED"), t("Failed to copy"));
        });
    }, [t]);

    React.useEffect(() => {
        setData(tableData);
    }, [tableData]);

    const columns = [
        columnHelper.accessor("reference", {
            id: "reference",
            header: () => (
                <Text fontSize={{ sm: "10px", lg: "12px" }} color="gray.400" fontWeight="600" textAlign="center">
                    {t("REFERENCE")}
                </Text>
            ),
            cell: (info) => {
                const reference = info.getValue() || t("N/A");
                const displayRef = reference && reference.length > 15
                    ? reference.substring(0, 6) + "..." + reference.substring(reference.length - 6)
                    : reference;
                return (
                    <Flex justify="center" align="center" gap={1}>
                        <Text
                            color={textColor}
                            fontSize="sm"
                            fontWeight="700"
                            fontFamily="monospace"
                            title={reference}
                            cursor="pointer"
                        >
                            {displayRef}
                        </Text>
                        {reference !== t("N/A") && (
                            <IconButton
                                size="sm"
                                icon={<CopyIcon />}
                                variant="ghost"
                                onClick={() => copyToClipboard(reference, t("Reference"))}
                                _hover={{ bg: bgHover }}
                            />
                        )}
                    </Flex>
                );
            },
        }),
        columnHelper.accessor("type", {
            id: "type",
            header: () => (
                <Text fontSize={{ sm: "10px", lg: "12px" }} color="gray.400" fontWeight="600" textAlign="center">
                    {t("TYPE")}
                </Text>
            ),
            cell: (info) => {
                const rawValue = info.getValue();
                const value = rawValue ? t(rawValue.toUpperCase()) : t("N/A");
                const typeMap = {
                    "purchase": { bg: "blue.100", color: "blue.800" },
                    "debit": { bg: "purple.100", color: "purple.800" },
                    "credit": { bg: "green.100", color: "green.800" },
                    "refund": { bg: "orange.100", color: "orange.800" },
                };
                const style = typeMap[rawValue?.toLowerCase()] || { bg: "gray.100", color: "gray.800" };
                return (
                    <Flex justify="center">
                        <Badge
                            bg={style.bg}
                            color={style.color}
                            px={3}
                            py={1}
                            borderRadius="full"
                            fontSize="11px"
                            fontWeight="700"
                            whiteSpace="nowrap"
                            textTransform="capitalize"
                        >
                            {value}
                        </Badge>
                    </Flex>
                );
            },
        }),
        columnHelper.accessor("narration", {
            id: "narration",
            header: () => (
                <Text fontSize={{ sm: "10px", lg: "12px" }} color="gray.400" fontWeight="600">
                    {t("NARRATION")}
                </Text>
            ),
            cell: (info) => {
                const value = info.getValue() || t("N/A");
                return (
                    <Text color={textColor} fontSize="sm" fontWeight="500" noOfLines={1}>
                        {value}
                    </Text>
                );
            },
        }),
        columnHelper.accessor("amount", {
            id: "amount",
            header: () => (
                <Text fontSize={{ sm: "10px", lg: "12px" }} color="gray.400" fontWeight="600" textAlign="center">
                    {t("AMOUNT")}
                </Text>
            ),
            cell: (info) => {
                const amount = info.getValue();
                const currency = info.row.original.currency || "NGN";
                return (
                    <Flex align="center" gap={2} justify="center">
                        <Text color={textColor} fontSize="sm" fontWeight="700">
                            {currency} {Number(amount || 0).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                        </Text>
                    </Flex>
                );
            },
        }),
        columnHelper.accessor("status", {
            id: "status",
            header: () => (
                <Text fontSize={{ sm: "10px", lg: "12px" }} color="gray.400" fontWeight="600" textAlign="center">
                    {t("STATUS")}
                </Text>
            ),
            cell: (info) => {
                const rawValue = info.getValue();
                const value = rawValue ? t(rawValue.toUpperCase()) : t("N/A");
                let icon = null;
                let color = "gray";

                if (rawValue?.toUpperCase() === "SUCCESSFUL") {
                    icon = <MdCheckCircle />;
                    color = "green";
                } else if (rawValue?.toUpperCase() === "FAILED") {
                    icon = <MdCancel />;
                    color = "red";
                } else if (rawValue?.toUpperCase() === "PENDING") {
                    icon = <MdOutlineError />;
                    color = "orange";
                }

                return (
                    <Flex justify="center" align="center" gap={2}>
                        {icon && <Flex color={`${color}.500`}>{icon}</Flex>}
                        <Badge
                            bg={`${color}.100`}
                            color={`${color}.800`}
                            px={3}
                            py={1}
                            borderRadius="full"
                            fontSize="11px"
                            fontWeight="700"
                            whiteSpace="nowrap"
                            textTransform="capitalize"
                        >
                            {value}
                        </Badge>
                    </Flex>
                );
            },
        }),
        columnHelper.accessor("createdAt", {
            id: "createdAt",
            header: () => (
                <Text fontSize={{ sm: "10px", lg: "12px" }} color="gray.400" fontWeight="600" textAlign="center">
                    {t("DATE")}
                </Text>
            ),
            cell: (info) => {
                const date = info.getValue();
                const formattedDate = date
                    ? new Date(date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                    })
                    : t("N/A");
                const formattedTime = date
                    ? new Date(date).toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                    })
                    : "";
                return (
                    <Flex direction="column" align="center" gap={1}>
                        <Text color={textColor} fontSize="sm" fontWeight="700">
                            {formattedDate}
                        </Text>
                        <Text color="gray.400" fontSize="xs" fontWeight="500">
                            {formattedTime}
                        </Text>
                    </Flex>
                );
            },
        }),
        columnHelper.accessor("reference", {
            id: "actions",
            header: () => (
                <Text fontSize={{ sm: "10px", lg: "12px" }} color="gray.400" fontWeight="600" textAlign="center">
                    {t("ACTIONS")}
                </Text>
            ),
            cell: (info) => (
                <Flex justify="center">
                    <Button
                        size="xs"
                        colorScheme="brand"
                        variant="outline"
                        leftIcon={<Icon as={ViewIcon} />}
                        fontSize="10px"
                        height="24px"
                        onClick={() => handleViewTransaction(info.row.original)}
                    >
                        {t("View")}
                    </Button>
                </Flex>
            ),
        }),
    ];

    const table = useReactTable({
        data: data,
        columns,
        state: { sorting, pagination: { pageSize, pageIndex } },
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        setPageSize: pageSize,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        debugTable: true,
    });

    React.useEffect(() => {
        if (table.getState().pagination.pageIndex !== 0) {
            table.setPageIndex(0);
        }
    }, [tableData, table]);

    const createPages = (count) =>
        Array.from({ length: count }, (_, i) => i + 1);

    return (
        <>
        <Card
            flexDirection="column"
            w="100%"
            px="0px"
            overflow="hidden"
        >
            <Flex px="25px" py="16px" justifyContent="space-between" align="center">
                <Text
                    color={textColor}
                    fontSize="22px"
                    fontWeight="700"
                    lineHeight="100%"
                >
                    {t("Card Transactions")}
                </Text>
            </Flex>

            <Box overflowX="auto">
                {data.length === 0 ? (
                    <Flex justifyContent="center" alignItems="center" height="300px">
                        <Text color={textColor} fontSize="sm" fontWeight="500">
                            {t("No results found")}! {t("Check")}{" "}
                            <NavLink style={{ color: "green" }} to={constants.urls.documentation}>
                                {t("API documentation")}
                            </NavLink>{" "}
                            {t("for more details")}
                        </Text>
                    </Flex>
                ) : (
                    <Table variant="simple" color="gray.500" mb="24px">
                        <Thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <Tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                        return (
                                            <Th
                                                key={header.id}
                                                colSpan={header.colSpan}
                                                pe="10px"
                                                borderColor={borderColor}
                                                cursor="pointer"
                                            >
                                                <Flex
                                                    justify="center"
                                                    align="center"
                                                    fontSize={{ sm: "10px", lg: "12px" }}
                                                    color="gray.400"
                                                >
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                                </Flex>
                                            </Th>
                                        );
                                    })}
                                </Tr>
                            ))}
                        </Thead>
                        <Tbody>
                            {table.getRowModel().rows.map((row) => {
                                return (
                                    <Tr key={row.id} _hover={{ bg: bgHover }}>
                                        {row.getVisibleCells().map((cell) => {
                                            return (
                                                <Td
                                                    key={cell.id}
                                                    fontSize={{ sm: "14px" }}
                                                    minW={{ sm: "150px", md: "200px", lg: "auto" }}
                                                    borderColor={borderColor}
                                                    py="12px"
                                                >
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext()
                                                    )}
                                                </Td>
                                            );
                                        })}
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                )}
            </Box>

            <Flex justify="space-between" align="center" px="25px" py="20px">
                <Flex align="center" gap={2}>
                    <Text fontSize="sm" color="gray.400">
                        {t("Show")}
                    </Text>
                    <Button
                        size="sm"
                        fontSize="sm"
                        variant={pageSize === 5 ? "solid" : "ghost"}
                        colorScheme={pageSize === 5 ? "brand" : "gray"}
                        onClick={() => table.setPageSize(5)}
                    >
                        5
                    </Button>
                    <Button
                        size="sm"
                        fontSize="sm"
                        variant={pageSize === 10 ? "solid" : "ghost"}
                        colorScheme={pageSize === 10 ? "brand" : "gray"}
                        onClick={() => table.setPageSize(10)}
                    >
                        10
                    </Button>
                    <Button
                        size="sm"
                        fontSize="sm"
                        variant={pageSize === 20 ? "solid" : "ghost"}
                        colorScheme={pageSize === 20 ? "brand" : "gray"}
                        onClick={() => table.setPageSize(20)}
                    >
                        20
                    </Button>
                    <Text fontSize="sm" color="gray.400">
                        {t("entries")}
                    </Text>
                </Flex>

                <Flex align="center" gap={2}>
                    <Button
                        size="sm"
                        onClick={() => table.previousPage()}
                        isDisabled={!table.getCanPreviousPage()}
                    >
                        {t("Previous")}
                    </Button>
                    <Flex align="center" gap={1}>
                        {createPages(table.getPageCount()).map((page) => (
                            <Button
                                key={page}
                                size="sm"
                                variant={
                                    table.getState().pagination.pageIndex === page - 1
                                        ? "solid"
                                        : "ghost"
                                }
                                colorScheme={
                                    table.getState().pagination.pageIndex === page - 1
                                        ? "brand"
                                        : "gray"
                                }
                                onClick={() => table.setPageIndex(page - 1)}
                            >
                                {page}
                            </Button>
                        ))}
                    </Flex>
                    <Button
                        size="sm"
                        onClick={() => table.nextPage()}
                        isDisabled={!table.getCanNextPage()}
                    >
                        {t("Next")}
                    </Button>
                </Flex>
            </Flex>
        </Card>
        <TransactionDetailsModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            transaction={selectedTransaction}
            t={t}
        />
        </>
    );
}
